/**
 * Converts contentEditable-style HTML to GFM markdown.
 * Used when sending chat messages so we store/display with react-markdown + remark-gfm + rehype-raw.
 * Inside list items, <br> is converted to "  \n" so Shift+Enter shows as a line break in the same item.
 * Blockquotes: inner newlines become `> text<br>more` so line breaks round-trip through markdown + rehype-raw.
 */

/**
 * Get text/markdown for a node. For <br> inside an <li> we output "  \n" (GFM line break).
 * @param {Node} node
 * @param {{ inListItem: boolean }} ctx
 * @returns {string}
 */
function nodeToMarkdown(node, ctx = { inListItem: false }) {
    if (node.nodeType === Node.TEXT_NODE) {
        return (node.textContent || '').replace(/\n/g, ' ').replace(/\u200b/g, '');
    }
    if (node.nodeType !== Node.ELEMENT_NODE) return '';

    const el = /** @type {Element} */ (node);
    const tag = (el.tagName || '').toLowerCase();
    const inLi = ctx.inListItem || tag === 'li';

    if (tag === 'br') {
        // GFM hard line break: two trailing spaces + newline
        return '  \n';
    }

    const childCtx = { inListItem: inLi };
    const children = () => {
        let out = '';
        for (const child of el.childNodes) {
            out += nodeToMarkdown(child, childCtx);
        }
        return out;
    };

    const inner = children();

    switch (tag) {
        case 'strong':
        case 'b':
            return inner ? `**${inner}**` : '';
        case 'em':
        case 'i':
            return inner ? `*${inner}*` : '';
        case 'u':
            return inner ? `<u>${inner}</u>` : '';
        case 's':
        case 'strike':
        case 'del':
            return inner ? `~~${inner}~~` : '';
        case 'code':
            return inner ? `\`${inner.replace(/`/g, '\\`')}\`` : '';
        case 'a': {
            const href = el.getAttribute('href') || '';
            return href && inner ? `[${inner.replace(/\]/g, '\\]')}](${href})` : inner;
        }
        case 'p':
        case 'div':
            return inner ? `${inner.trim()}\n\n` : '\n\n';
        case 'pre':
            // IMPORTANT: <pre><code>...</code></pre> should serialize as a fenced code block.
            // Do NOT recurse into <code> here, otherwise the <code> rule wraps backticks and corrupts the fence.
            try {
                const raw = (el.textContent || '').replace(/\u200b/g, '');
                if (!raw.trim()) return '';
                // Preserve newlines; trim only trailing whitespace so the fence closes cleanly.
                const body = raw.replace(/\s+$/g, '');
                return `\n\`\`\`\n${body}\n\`\`\`\n\n`;
            } catch {
                return inner ? `\n\`\`\`\n${inner}\n\`\`\`\n\n` : '';
            }
        case 'blockquote': {
            const raw = inner.trim();
            if (!raw) return '';
            // Prefix each line with '> ' so blockquote renders correctly in markdown
            const lines = raw.split('\n');
            return lines.map((l) => `> ${l}`).join('\n') + '\n\n';
        }
        case 'ul': {
            const items = [];
            el.querySelectorAll(':scope > li').forEach((li) => {
                const text = nodeToMarkdown(li, { inListItem: true }).trim();
                if (text) items.push(`- ${text}`);
            });
            return items.length ? items.join('\n') + '\n\n' : '';
        }
        case 'ol': {
            const items = [];
            el.querySelectorAll(':scope > li').forEach((li, i) => {
                const text = nodeToMarkdown(li, { inListItem: true }).trim();
                if (text) items.push(`${i + 1}. ${text}`);
            });
            return items.length ? items.join('\n') + '\n\n' : '';
        }
        case 'li':
            return inner.trim() + '\n';
        case 'h1':
            return inner ? `# ${inner.trim()}\n\n` : '';
        case 'h2':
            return inner ? `## ${inner.trim()}\n\n` : '';
        case 'h3':
            return inner ? `### ${inner.trim()}\n\n` : '';
        case 'h4':
            return inner ? `#### ${inner.trim()}\n\n` : '';
        case 'h5':
            return inner ? `##### ${inner.trim()}\n\n` : '';
        case 'h6':
            return inner ? `###### ${inner.trim()}\n\n` : '';
        case 'hr':
            return '---\n\n';
        default:
            return inner;
    }
}

/**
 * Convert HTML string to GFM markdown.
 * List items: <br> becomes "  \n" so line breaks render in the same item (Shift+Enter).
 *
 * @param {string} html - HTML from contentEditable (or similar)
 * @returns {string} Markdown string
 */
export function htmlToMarkdown(html) {
    if (typeof html !== 'string' || !html.trim()) return '';
    const normalized = html.trim();
    if (normalized === '<br>' || normalized === '<br/>') return '';

    try {
        const parser = new DOMParser();
        const doc = parser.parseFromString(normalized, 'text/html');
        const body = doc.body;
        if (!body) return '';

        let out = '';
        for (const child of body.childNodes) {
            out += nodeToMarkdown(child, { inListItem: false });
        }
        return out.replace(/\n{3,}/g, '\n\n').trim();
    } catch {
        return html.replace(/<[^>]+>/g, '').trim();
    }
}
